import MappingInfoStore, {
    FT_CHILD,
    FT_GLOBAL_REFERENCE_ONLY,
    FT_GLOBAL_REFERENCE_OR_CREATE,
    FT_MAGIC_ROOT,
    MAP_TARGET,
    ORDER_ITEM_TYPE,
} from "data/meta/MappingInfoStore";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import {
    getPairedIdLO,
    isExcluded,
} from "data/MappingStore";
import LiveMap from "data/util/LiveMap";
import ClientId from "data/ClientId";
import parseId from "data/util/parseId";
import asArray from "data/util/asArray";


// from RecordMapping
export const isMappingEnabled = (instance, scriptOrInfo) => {
    if (scriptOrInfo == null) return true;
    const script = scriptOrInfo.hasOwnProperty("fieldKey")
        ? scriptOrInfo.enableScript
        : scriptOrInfo;
    if (script == null) return true;
    try {
        return script(instance);
    } catch (e) {
        return true;
    }
};

// from RecordMapping
export const getValue = (instance, key, script) => {
    if (key != null && key.hasOwnProperty("fieldKey")) {
        // it's a whole FieldMappingInfo - chop it up
        script = key.sourceScript;
        key = key.sourceFieldKey;
    }
    if (script) {
        try {
            return script(instance);
        } catch (e) {
            console.warn(`Error in mapping script for '${instance.getKey()}.${key}'`, e);
            return null;
        }
    }
    if (key) {
        return instance.get(key);
    }
    // neither a script nor a key, which means we're "spreading" a
    // a single source record across a target hierarchy
    return instance;
};

// from FieldMapping
export const sanitizeValue = (info, val) => {
    if (val == null) return val;
    if (info.type === "ChoiceField") {
        if (info.multiValued) {
            if (! (val instanceof Array)) {
                return [val.toUpperCase()];
            } else {
                // make sure all selected values are upper case HJC-97
                return val.map(v => v.toUpperCase());
            }
        } else {
            if (val instanceof Array) {
                return val.length === 0 ? null : val[0].toUpperCase();
            } else {
                // make sure the value is upper case HJC-97
                return val.toUpperCase();
            }
        }
    } else if (info.type === "StringField") {
        if (val instanceof Array) {
            return val.join(",");
        }
    }
    return val;
};

class MappingVisitor {

    constructor(mappingState, tableAccess, snapAccess) {
        this.state = mappingState;
        this._initialState = mappingState;
        this.access = tableAccess;
        this._snapshot = snapAccess;
        this.old = new LiveMap(key => {
            const [ek, idString] = key.split("#");
            const id = parseId(idString);
            if (!this._snapshot.hasEntityRecord(ek, id)) return null;
            return this._snapshot.getEntityProxy(ek, id);
        });
    }

    execute(ctx) {
        this.doRecord(
            ctx || {},
            this.access.getEntityProxy(
                this.state.target.entityKey,
                this.state.target.id,
            ),
            false,
            this.state.source,
            MAP_TARGET,
            null,
        );
        return this;
    }

    doRecord(ctx, target, excluded, source, type, edgeInfo) {
        const mappingInfo = MappingInfoStore.getInfo(
            target.entityKey,
            source.entityKey,
        );

        for (const it of mappingInfo.formOrder) {
            switch (it.type) {
                case ORDER_ITEM_TYPE.FIELD:
                    this._doRecordField(ctx, target, source, it.field);
                    break;
                case ORDER_ITEM_TYPE.CHILD:
                    this._doRecordChild(ctx, target, source, it.child);
                    break;
                default:
                    throw new Error(`Unknown '${it.type}' item type?`);
            }
        }

        for (const cta of mappingInfo.crossTrees) {
            this._doRecordCTA(ctx, target, source, cta);
        }
    }

    _doRecordField(ctx, target, source, f) {
        if (!isMappingEnabled(source, f)) return;
        const fieldInfo = entityFieldMetaStore.getInfo(
            target.entityKey,
            f.fieldKey,
        );
        // this may need to change if we need to support selecting a subset
        // of a multi-valued ChoiceField's options...
        const excludeField = isExcluded(this.state, target.id, fieldInfo.logicalColumn);
        const cctx = {
            ...ctx,
            fieldMappingInfo: f,
        };
        if (!this.shouldDoField(ctx, target, excludeField, fieldInfo)) return;
        const rawValue = sanitizeValue(fieldInfo, getValue(source, f));
        this.doField(cctx, target, excludeField, fieldInfo, rawValue);
    }

    _doRecordChild(ctx, target, source, c) {
        if (!isMappingEnabled(source, c.edge)) return;
        const fieldInfo = entityFieldMetaStore.getInfo(
            target.entityKey,
            c.edge.fieldKey,
        );
        const excludeField = isExcluded(this.state, target.id, fieldInfo.logicalColumn);
        const cctx = {
            ...ctx,
            childMappingInfo: c,
        };
        if (!this.shouldDoChild(cctx, target, excludeField, fieldInfo, c.type)) return;
        const items = asArray(getValue(source, c.edge));
        const rawValue = this.__cleanMultiplicity(fieldInfo, items);
        if (c.type === FT_MAGIC_ROOT) {
            this.doMagicRoot(cctx, target, excludeField, fieldInfo, rawValue);
        } else if (c.type === FT_GLOBAL_REFERENCE_ONLY) {
            this.doGlobalAssoc(
                cctx,
                target,
                excludeField,
                fieldInfo,
                rawValue,
                false
            );
        } else if (c.type === FT_GLOBAL_REFERENCE_OR_CREATE) {
            this.doGlobalAssoc(
                cctx,
                target,
                excludeField,
                fieldInfo,
                rawValue,
                true
            );
        } else if (c.type === FT_CHILD) {
            if (fieldInfo.autoManageKey != null) {
                const amfi = entityFieldMetaStore.getInfo(fieldInfo.otherEntityKey, fieldInfo.autoManageKey);
                const kids = MappingInfoStore.getInfo(c.targetKey, c.sourceKey).children;
                if (kids == null) return;
                // todo: index this
                const amem = kids.find(it =>
                    it.edge.fieldKey === fieldInfo.autoManageKey);
                if (amem == null) return; // AMK isn't mapped
                this.doAutomanaged(
                    cctx,
                    target,
                    excludeField,
                    fieldInfo,
                    amfi,
                    rawValue,
                    s =>
                        // Blindly keep assuming the managed-by field's mapping is a copy
                        s.get(amem.edge.sourceFieldKey)
                );
            } else { // normal composition
                this.doComposition(
                    cctx,
                    target,
                    excludeField,
                    fieldInfo,
                    rawValue,
                );
            }
        } else {
            throw new Error(`Unknown '${c.type}' child type`);
        }
    }

    _doRecordCTA(ctx, target, source, cta) {
        // Walk the sourceOwnerPath from the current source record to get
        // the owners of the CTA, named by sourceFieldKey.
        const sourceOwners = cta.sourceOwnerPath.reduce((curr, fk) => {
            const next = [];
            curr.forEach(c =>
                this.__forEach(c.get(fk), n =>
                    next.push(n)));
            return next;
        }, [source]);
        if (sourceOwners.length === 0) return;

        let targetFieldInfo;
        let ctaFieldInfo;
        let targetRecord;
        if(cta.targetOwnerPath.length === 0) {
            // Get the fieldInfo that the CTA edge points at.
            ctaFieldInfo = entityFieldMetaStore.getInfo(
                target.entityKey,
                cta.fieldKey,
            );
            targetFieldInfo = {
                ...ctaFieldInfo,
                otherEntityKey: target.entityKey
            };
            targetRecord = target;
        } else {
            // Get the fieldInfo that the CTA path points at.
            targetFieldInfo = cta.targetOwnerPath.reduce(
                (curr, fk) =>
                    entityFieldMetaStore.getInfo(curr.otherEntityKey, fk),
                {otherEntityKey: target.entityKey},
            );
            ctaFieldInfo = entityFieldMetaStore.getInfo(
                targetFieldInfo.otherEntityKey,
                cta.fieldKey,
            );
            // we want the owner of the CTA, not the CTA record itself
            targetRecord = cta.targetOwnerPath.slice(0, cta.targetOwnerPath.length - 1).reduce(
                (curr, fk) => curr.get(fk),
                target
            );
        }

        // Find the corresponding target record for each source record, if
        // one exists, by walking the metadata from the current target's
        // entity down to one shy of the last entry in targetOwnerPath, and
        // then using the last one to query the id pair.
        const ownerPairs = sourceOwners.map(so => {
            const lo = getPairedIdLO(
                this.state,
                so.id,
                targetRecord.getKey(),
                targetFieldInfo.key,
            );
            if (!lo.hasValue()) return [so, null];
            const tid = lo.getValueEnforcing();
            const to = this.access.hasEntityRecord(
                targetFieldInfo.otherEntityKey,
                tid,
            )
                ? this.access.getEntityProxy(
                    targetFieldInfo.otherEntityKey,
                    tid,
                )
                : null;
            return [so, to];
        }).filter(p => p[1] != null);
        if (ownerPairs.length === 0) return;

        const cctx = {
            ...ctx,
            active: true, // CTAs are always active, but just for symmetry
            crossTreeAssoc: cta,
        };
        ownerPairs.forEach(([so, to]) => {
            const excludeField = isExcluded(this.state, to.id, ctaFieldInfo.logicalColumn);
            if (!this.shouldDoCrossTree(cctx, to, excludeField, ctaFieldInfo)) return;
            this.doCrossTree(
                cctx,
                to,
                excludeField,
                ctaFieldInfo,
                so.get(cta.sourceFieldKey)
            );
        });
    }

    doField(ctx, target, excluded, fieldInfo, sourceValue) {
        /* will be overloaded */
    }

    doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue) {
        this.__doChildRecordHelper(
            ctx,
            target,
            fieldInfo,
            sourceValue,
            FT_MAGIC_ROOT,
        );
    }

    doGlobalAssoc(
        ctx,
        target,
        excluded,
        fieldInfo,
        sourceValue,
        createIfMissing,
    ) {
        // only cascade for created globals - existing ones are only referenced,
        // not updated, so their tree is irrelevant.
        let creationHappened = false;
        this.__forEach(sourceValue, it => {
            if (creationHappened || !createIfMissing) return;
            const lo = this.__getTargetIdLO(it, target, fieldInfo);
            if (!lo.hasValue()) return;
            creationHappened = ClientId.is(lo.getValueEnforcing());
        });
        if (creationHappened) {
            this.__doChildRecordHelper(
                ctx,
                target,
                fieldInfo,
                sourceValue,
                createIfMissing
                    ? FT_GLOBAL_REFERENCE_OR_CREATE
                    : FT_GLOBAL_REFERENCE_ONLY,
            );
        }
    }

    doComposition(ctx, target, excluded, fieldInfo, sourceValue) {
        if (sourceValue == null) return;
        this.__forEach(sourceValue, it => {
            const lo = this.__getTargetIdLO(it, target, fieldInfo);
            if (!lo.hasValue()) return;
            const id = lo.getValueEnforcing();
            if (isExcluded(this.state, target.id, fieldInfo.logicalColumn, id)) {
                this.doExcludedChild(ctx, target, fieldInfo, id, it);
            } else {
                this.doComposedChild(
                    ctx,
                    target,
                    fieldInfo,
                    this.access.getEntityProxy(fieldInfo.otherEntityKey, id),
                    it,
                );
            }
        });
    }

    doComposedChild(ctx, targetParent, edgeInfo, target, source) {
        return this.doRecord(ctx, target, false, source, FT_CHILD, edgeInfo);
    }

    doExcludedChild(ctx, targetParent, edgeInfo, targetId, source) {
        /* will be overloaded */
    }

    __getAutomanagedTargetLookup(target, fieldInfo) {
        let targets = target.get(fieldInfo.key);
        if (targets == null) return;
        if (!Array.isArray(targets)) targets = [targets]; // that's weird
        if (targets.length === 0) return;

        const targetLookup = {}; // target global id -> target child
        targets.forEach(t => {
            const targetGlobal = t.get(fieldInfo.autoManageKey);
            if (targetGlobal == null) return;
            targetLookup[targetGlobal.id] = t;
        });
        return targetLookup;
    }

    __getAutomanagedTarget(target, source, fieldInfo) {
        const lo = this.__getPairedIdLO(
            source.id,
            target.entityKey, // composition, so no ID
            fieldInfo.key,
        );
        if (!lo.hasValue()) return null;
        return this.access.getEntityProxy(
            fieldInfo.otherEntityKey,
            lo.getValueEnforcing()
        );
    }

    // `sourceManagedByGetter` accepts a source record, and returns the value of
    // being mapped onto the target child's auto-managed-by field (named by
    // `fieldInfo.autoManageKey`). I.e., it gets the record
    doAutomanaged(
        ctx,
        target,
        excluded,
        fieldInfo,
        autoManagedByFieldInfo,
        sourceValue,
        sourceManagedByGetter,
    ) {
        this.__doChildRecordHelper(
            ctx,
            target,
            fieldInfo,
            sourceValue,
            FT_CHILD,
        );
    }

    doCrossTree(ctx, target, excluded, fieldInfo, sourceValue) {
        /* will be overloaded */
    }

    shouldDoField(ctx, target, excluded, fieldInfo) {
        return target.isFieldEnabled(fieldInfo);
    }

    shouldDoChild(ctx, target, excluded, fieldInfo, type) {
        return target.isFieldEnabled(fieldInfo);
    }

    shouldDoCrossTree(ctx, target, excluded, fieldInfo) {
        return target.isFieldEnabled(fieldInfo);
    }

    __cleanMultiplicity(fieldInfo, items) {
        return fieldInfo.multiValued
            ? items
            : items.length === 0 ? null : items[0];
    }

    __oldValue(target, fieldInfo) {
        const ep = this.old.get(target.getKey());
        return ep == null ? null : ep.get(fieldInfo.key);
    }

    __doChildRecordHelper(ctx, target, fieldInfo, sourceValue, type) {
        if (sourceValue == null) return;
        this.__forEach(sourceValue, it => {
            const lo = this.__getTargetIdLO(it, target, fieldInfo);
            if (!lo.hasValue()) return;
            const id = lo.getValueEnforcing();
            this.doRecord(
                ctx,
                this.access.getEntityProxy(fieldInfo.otherEntityKey, id),
                isExcluded(this.state, target.id, fieldInfo.logicalColumn, id),
                it,
                type,
                fieldInfo,
            );
        });
    }

    // Note that this operates off the _initial_ state passed to the visitor; it
    // doesn't update during the walk.
    __getTargetIdLO(sourceRecord, target, fieldInfo) {
        return this.__getPairedIdLO(
            sourceRecord.id,
            target.getKey(),
            fieldInfo.key,
        );
    }

    // Note that this operates off the _initial_ state passed to the visitor; it
    // doesn't update during the walk.
    __getPairedIdLO(sourceId, targetRecordKey, targetFieldKey) {
        return getPairedIdLO(
            this._initialState,
            sourceId,
            targetRecordKey,
            targetFieldKey,
        );
    }

    /**
     * I iterate over a single item or a collection of them, performing work on
     * each item. This is a common idiom w/in mapping (just process the child
     * records - I don't care about multiplicity).
     */
    __forEach(itemOrItems, work) {
        if (itemOrItems == null) return;
        for (const it of asArray(itemOrItems)) {
            work(it);
        }
    }

}

export default MappingVisitor;



// WEBPACK FOOTER //
// ./app/data/util/MappingVisitor.js